import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, MessageSquare, Users, Play, Pause, Plus, 
  ChevronRight, Settings, Trash2, BarChart3, Zap,
  Clock, Target, TrendingUp, Send, Eye, MousePointer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sequences');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState(null);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [campaignsRes, sequencesRes, templatesRes, overviewRes] = await Promise.all([
        fetch(`${API_URL}/api/nurturing/campaigns`, { headers }),
        fetch(`${API_URL}/api/nurturing/sequences`, { headers }),
        fetch(`${API_URL}/api/nurturing/templates`, { headers }),
        fetch(`${API_URL}/api/nurturing/analytics/overview`, { headers })
      ]);

      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        setCampaigns(data.campaigns || []);
      }
      
      if (sequencesRes.ok) {
        const data = await sequencesRes.json();
        setSequences(data.sequences || []);
      }

      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }

      if (overviewRes.ok) {
        const data = await overviewRes.json();
        setOverview(data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load campaigns data');
    } finally {
      setLoading(false);
    }
  };

  const createSequence = async (name, description) => {
    try {
      const res = await fetch(`${API_URL}/api/nurturing/sequences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, description, trigger_type: 'manual' })
      });

      if (res.ok) {
        toast.success('Sequence created successfully');
        fetchData();
        setShowCreateModal(false);
      } else {
        toast.error('Failed to create sequence');
      }
    } catch (error) {
      toast.error('Failed to create sequence');
    }
  };

  const applyTemplate = async (templateId, name) => {
    try {
      const res = await fetch(`${API_URL}/api/nurturing/templates/${templateId}/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Template applied! ${data.steps_created} steps created.`);
        fetchData();
        setShowTemplateModal(false);
      } else {
        toast.error('Failed to apply template');
      }
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const toggleSequenceStatus = async (sequenceId, currentStatus) => {
    const endpoint = currentStatus === 'active' ? 'pause' : 'activate';
    try {
      const res = await fetch(`${API_URL}/api/nurturing/sequences/${sequenceId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(`Sequence ${endpoint}d successfully`);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || `Failed to ${endpoint} sequence`);
      }
    } catch (error) {
      toast.error(`Failed to ${endpoint} sequence`);
    }
  };

  const deleteSequence = async (sequenceId) => {
    if (!window.confirm('Are you sure you want to delete this sequence?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/nurturing/sequences/${sequenceId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Sequence deleted');
        fetchData();
      } else {
        toast.error('Failed to delete sequence');
      }
    } catch (error) {
      toast.error('Failed to delete sequence');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'paused': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'draft': return 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
      default: return 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Lead Nurturing</h1>
        <p className="text-emerald-300/70">Automate your lead follow-ups with email and WhatsApp sequences</p>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Zap className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview.sequences?.active || 0}</p>
                  <p className="text-sm text-emerald-300/70">Active Sequences</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview.enrollments?.active || 0}</p>
                  <p className="text-sm text-emerald-300/70">Active Enrollments</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Send className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview.messages?.sent || 0}</p>
                  <p className="text-sm text-emerald-300/70">Messages Sent</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Eye className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{overview.messages?.open_rate || 0}%</p>
                  <p className="text-sm text-emerald-300/70">Open Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['sequences', 'templates'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab 
                ? 'bg-emerald-600 text-white' 
                : 'bg-[#0a3d2e] text-emerald-300/70 hover:bg-slate-700'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'sequences' && (
          <motion.div
            key="sequences"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {/* Actions */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Your Sequences</h2>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowTemplateModal(true)}
                  variant="outline"
                  className="border-emerald-800/30 text-slate-300 hover:bg-[#0a3d2e]"
                  data-testid="use-template-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Use Template
                </Button>
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="create-sequence-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Sequence
                </Button>
              </div>
            </div>

            {/* Sequences Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
              </div>
            ) : sequences.length === 0 ? (
              <Card className="bg-[#052e22]/50 border-emerald-800/30 border-dashed">
                <CardContent className="p-12 text-center">
                  <Mail className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No sequences yet</h3>
                  <p className="text-emerald-300/70 mb-4">Create your first sequence to start nurturing leads automatically</p>
                  <div className="flex gap-2 justify-center">
                    <Button 
                      onClick={() => setShowTemplateModal(true)}
                      variant="outline"
                      className="border-emerald-800/30 text-slate-300"
                    >
                      Start from Template
                    </Button>
                    <Button 
                      onClick={() => setShowCreateModal(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      Create from Scratch
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sequences.map((sequence) => (
                  <Card 
                    key={sequence.id} 
                    className="bg-[#052e22]/50 border-emerald-800/30 hover:border-emerald-800/30 transition-all cursor-pointer"
                    data-testid={`sequence-card-${sequence.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-white">{sequence.name}</h3>
                          <p className="text-sm text-emerald-300/70 mt-1">{sequence.description || 'No description'}</p>
                        </div>
                        <Badge className={`${getStatusColor(sequence.status)} border`}>
                          {sequence.status}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-emerald-300/70 mb-4">
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          <span>{sequence.total_steps || 0} steps</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{sequence.total_enrolled || 0} enrolled</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSequenceStatus(sequence.id, sequence.status);
                          }}
                          className={sequence.status === 'active' 
                            ? 'bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30' 
                            : 'bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30'
                          }
                          data-testid={`toggle-sequence-${sequence.id}`}
                        >
                          {sequence.status === 'active' ? (
                            <><Pause className="w-4 h-4 mr-1" /> Pause</>
                          ) : (
                            <><Play className="w-4 h-4 mr-1" /> Activate</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-300/70 hover:text-white"
                          onClick={() => setSelectedSequence(sequence)}
                          data-testid={`view-sequence-${sequence.id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSequence(sequence.id);
                          }}
                          data-testid={`delete-sequence-${sequence.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'templates' && (
          <motion.div
            key="templates"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <h2 className="text-xl font-semibold text-white mb-6">Ready-to-Use Templates</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card 
                  key={template.id}
                  className="bg-[#052e22]/50 border-emerald-800/30 hover:border-emerald-500/50 transition-all"
                  data-testid={`template-card-${template.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <Target className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{template.name}</h3>
                        <Badge variant="outline" className="text-xs text-emerald-300/70 border-emerald-800/30">
                          {template.target_audience} leads
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-emerald-300/70 mb-4">{template.description}</p>
                    
                    <div className="flex items-center gap-2 text-sm text-emerald-300/70 mb-4">
                      <Clock className="w-4 h-4" />
                      <span>{template.steps?.length || 0} steps</span>
                    </div>

                    <Button
                      onClick={() => applyTemplate(template.id, template.name)}
                      className="w-full bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border border-emerald-500/30"
                      data-testid={`apply-template-${template.id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Use This Template
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Sequence Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateSequenceModal
            onClose={() => setShowCreateModal(false)}
            onCreate={createSequence}
          />
        )}
      </AnimatePresence>

      {/* Sequence Detail Modal */}
      <AnimatePresence>
        {selectedSequence && (
          <SequenceDetailModal
            sequence={selectedSequence}
            onClose={() => setSelectedSequence(null)}
            onRefresh={fetchData}
            token={token}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Create Sequence Modal Component
function CreateSequenceModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a sequence name');
      return;
    }
    onCreate(name, description);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#052e22] border border-emerald-800/30 rounded-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-semibold text-white mb-4">Create New Sequence</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-emerald-300/70 mb-2">Sequence Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Hot Lead Welcome Series"
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              data-testid="sequence-name-input"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-emerald-300/70 mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this sequence for?"
              rows={3}
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 resize-none"
              data-testid="sequence-description-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={onClose}
              className="text-emerald-300/70"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-sequence-submit"
            >
              Create Sequence
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Sequence Detail Modal Component
function SequenceDetailModal({ sequence, onClose, onRefresh, token }) {
  const [steps, setSteps] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddStep, setShowAddStep] = useState(false);

  useEffect(() => {
    fetchSequenceDetails();
  }, [sequence.id]);

  const fetchSequenceDetails = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/nurturing/sequences/${sequence.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSteps(data.steps || []);
        setAnalytics(data.analytics || null);
      }
    } catch (error) {
      console.error('Failed to fetch sequence details:', error);
    } finally {
      setLoading(false);
    }
  };

  const addStep = async (stepData) => {
    try {
      const res = await fetch(`${API_URL}/api/nurturing/sequences/${sequence.id}/steps`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sequence_id: sequence.id,
          step_order: steps.length + 1,
          ...stepData
        })
      });

      if (res.ok) {
        toast.success('Step added successfully');
        fetchSequenceDetails();
        setShowAddStep(false);
        onRefresh();
      } else {
        toast.error('Failed to add step');
      }
    } catch (error) {
      toast.error('Failed to add step');
    }
  };

  const deleteStep = async (stepId) => {
    if (!window.confirm('Delete this step?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/nurturing/steps/${stepId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Step deleted');
        fetchSequenceDetails();
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to delete step');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#052e22] border border-emerald-800/30 rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-white">{sequence.name}</h3>
            <p className="text-sm text-emerald-300/70">{sequence.description || 'No description'}</p>
          </div>
          <Button variant="ghost" onClick={onClose} className="text-emerald-300/70">
            ✕
          </Button>
        </div>

        {/* Analytics */}
        {analytics && (
          <div className="grid grid-cols-4 gap-3 mb-6">
            <div className="bg-[#0a3d2e]/50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-white">{analytics.enrollments?.total || 0}</p>
              <p className="text-xs text-emerald-300/70">Enrolled</p>
            </div>
            <div className="bg-[#0a3d2e]/50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-white">{analytics.messages?.sent || 0}</p>
              <p className="text-xs text-emerald-300/70">Sent</p>
            </div>
            <div className="bg-[#0a3d2e]/50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-emerald-400">{analytics.messages?.open_rate || 0}%</p>
              <p className="text-xs text-emerald-300/70">Open Rate</p>
            </div>
            <div className="bg-[#0a3d2e]/50 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-emerald-400">{analytics.messages?.click_rate || 0}%</p>
              <p className="text-xs text-emerald-300/70">Click Rate</p>
            </div>
          </div>
        )}

        {/* Steps */}
        <div className="mb-4 flex items-center justify-between">
          <h4 className="font-medium text-white">Steps ({steps.length})</h4>
          <Button 
            size="sm"
            onClick={() => setShowAddStep(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-step-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Step
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : steps.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-emerald-800/30 rounded-lg">
            <Mail className="w-10 h-10 text-slate-600 mx-auto mb-2" />
            <p className="text-emerald-300/70">No steps yet. Add your first step to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className="bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg p-4 flex items-start gap-4"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600/20 flex items-center justify-center text-emerald-400 font-medium">
                  {index + 1}
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={step.channel === 'email' 
                      ? 'bg-blue-500/20 text-blue-400' 
                      : 'bg-emerald-500/20 text-emerald-400'
                    }>
                      {step.channel === 'email' ? <Mail className="w-3 h-3 mr-1" /> : <MessageSquare className="w-3 h-3 mr-1" />}
                      {step.channel}
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {step.delay_days > 0 || step.delay_hours > 0 
                        ? `Wait ${step.delay_days}d ${step.delay_hours}h`
                        : 'Immediate'
                      }
                    </span>
                  </div>
                  {step.subject && (
                    <p className="font-medium text-white text-sm mb-1">{step.subject}</p>
                  )}
                  <p className="text-sm text-emerald-300/70 line-clamp-2">{step.content}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 flex-shrink-0"
                  onClick={() => deleteStep(step.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add Step Form */}
        {showAddStep && (
          <AddStepForm 
            onCancel={() => setShowAddStep(false)}
            onSubmit={addStep}
          />
        )}
      </motion.div>
    </motion.div>
  );
}

// Add Step Form Component
function AddStepForm({ onCancel, onSubmit }) {
  const [channel, setChannel] = useState('email');
  const [delayDays, setDelayDays] = useState(0);
  const [delayHours, setDelayHours] = useState(0);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [useAI, setUseAI] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error('Please enter message content');
      return;
    }
    if (channel === 'email' && !subject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    onSubmit({
      channel,
      delay_days: delayDays,
      delay_hours: delayHours,
      subject: channel === 'email' ? subject : null,
      content,
      use_ai_personalization: useAI
    });
  };

  return (
    <div className="mt-4 p-4 bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg">
      <h5 className="font-medium text-white mb-4">Add New Step</h5>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-emerald-300/70 mb-1">Channel</label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
              data-testid="step-channel-select"
            >
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm text-emerald-300/70 mb-1">Days</label>
              <input
                type="number"
                min="0"
                value={delayDays}
                onChange={(e) => setDelayDays(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
                data-testid="step-delay-days"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-emerald-300/70 mb-1">Hours</label>
              <input
                type="number"
                min="0"
                value={delayHours}
                onChange={(e) => setDelayHours(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white"
                data-testid="step-delay-hours"
              />
            </div>
          </div>
        </div>

        {channel === 'email' && (
          <div className="mb-4">
            <label className="block text-sm text-emerald-300/70 mb-1">Subject Line</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g., Quick question, {name}?"
              className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500"
              data-testid="step-subject-input"
            />
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm text-emerald-300/70 mb-1">Message Content</label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={channel === 'email' 
              ? "Hi {name},\n\nYour email content here..."
              : "Hi {name}! Your WhatsApp message here..."
            }
            rows={5}
            className="w-full px-3 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 resize-none"
            data-testid="step-content-input"
          />
          <p className="text-xs text-gray-500 mt-1">Use {'{name}'}, {'{company}'}, {'{industry}'} for personalization</p>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="useAI"
            checked={useAI}
            onChange={(e) => setUseAI(e.target.checked)}
            className="rounded border-slate-600 bg-[#0a3d2e] text-emerald-600"
          />
          <label htmlFor="useAI" className="text-sm text-emerald-300/70">
            Use AI to personalize content for each lead
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} className="text-emerald-300/70">
            Cancel
          </Button>
          <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-step-btn">
            Add Step
          </Button>
        </div>
      </form>
    </div>
  );
}
